import { cn } from "../../lib/utils";
import type { BuildStatus } from "../../repositories/buildRepository";

interface BuildStatusBadgeProps {
  status: BuildStatus;
  className?: string;
}

const statusConfig: Record<
  BuildStatus,
  { label: string; icon: string; className: string; showPulse?: boolean }
> = {
  pending: {
    label: "Pending",
    icon: "schedule",
    className: "bg-amber-50 text-amber-700 border-amber-200",
  },
  claimed: {
    label: "Claimed",
    icon: "hourglass_empty",
    className: "bg-blue-50 text-blue-700 border-blue-200",
  },
  building: {
    label: "Building",
    icon: "sync",
    className: "bg-blue-50 text-blue-700 border-blue-200",
    showPulse: true,
  },
  uploading: {
    label: "Uploading",
    icon: "cloud_upload",
    className: "bg-amber-50 text-amber-700 border-amber-200",
  },
  completed: {
    label: "Completed",
    icon: "check_circle",
    className: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  failed: {
    label: "Failed",
    icon: "error",
    className: "bg-red-50 text-red-700 border-red-200",
  },
  cancelled: {
    label: "Cancelled",
    icon: "cancel",
    className: "bg-gray-50 text-gray-700 border-gray-200",
  },
};

export function BuildStatusBadge({ status, className }: BuildStatusBadgeProps) {
  const config = statusConfig[status];

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border",
        config.className,
        className
      )}
    >
      {config.showPulse ? (
        <span className="relative flex h-2 w-2 mr-1">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
        </span>
      ) : (
        <span className="material-symbols-outlined text-[14px]">{config.icon}</span>
      )}
      {config.label}
    </span>
  );
}
